const hasMessageKey = (e: unknown): e is { message: unknown } => {
  return (
    typeof e === 'object' &&
    e !== null &&
    'message' in e &&
    Object.prototype.hasOwnProperty.call(e, 'message')
  );
};

export const getErrorMessage = (
  err: unknown,
  fallback = 'Something went wrong'
): string => {
  if (err instanceof Error) return err.message;

  if (typeof err === 'string') return err;

  if (hasMessageKey(err)) {
    const msg = err.message;

    return typeof msg === 'string' ? msg : String(msg);
  }

  return fallback;
};

export enum VantifyOperation {
  FETCH_USERS = 'fetchUsers',
  FETCH_SITES = 'fetchSites',
  FETCH_SITE_DETAILS = 'fetchSiteDetails',
  FETCH_WORK_ORDER_DETAILS = 'fetchWorkOrderDetails',
  FETCH_WORK_ORDERS = 'fetchWorkOrders',
  FETCH_WORK_ORDER_FILES = 'fetchWorkOrderFiles',
  CREATE_WORK_ORDER = 'createWorkOrder',
  CANCEL_WORK_ORDER = 'cancelWorkOrder',
  ADD_WORK_ORDER_ATTACHMENT = 'addWorkOrderAttachment',
  DELETE_WORK_ORDER_ATTACHMENT = 'deleteWorkOrderAttachment',
  UPLOAD_FILE = 'uploadFile',
  FETCH_SITE_TENANTS = 'fetchSiteTenants',
  FETCH_CATEGORIES = 'fetchCategories',
  FETCH_SUBCATEGORIES = 'fetchSubCategories',
  FETCH_SERVICE_PROVIDERS = 'fetchServiceProviders',
  FETCH_SITE_ASSOCIATES = 'fetchSiteAssociates',
  ACCEPT_WORK_ORDER_APPROVAL = 'acceptWorkOrderApproval',
}

export type LoadingKeys = VantifyOperation;

export const loadingInitial: Record<LoadingKeys, boolean> = {
  [VantifyOperation.FETCH_USERS]: false,
  [VantifyOperation.FETCH_SITES]: false,
  [VantifyOperation.FETCH_SITE_DETAILS]: false,
  [VantifyOperation.FETCH_WORK_ORDER_DETAILS]: false,
  [VantifyOperation.FETCH_WORK_ORDERS]: false,
  [VantifyOperation.FETCH_WORK_ORDER_FILES]: false,
  [VantifyOperation.CREATE_WORK_ORDER]: false,
  [VantifyOperation.CANCEL_WORK_ORDER]: false,
  [VantifyOperation.ADD_WORK_ORDER_ATTACHMENT]: false,
  [VantifyOperation.DELETE_WORK_ORDER_ATTACHMENT]: false,
  [VantifyOperation.UPLOAD_FILE]: false,
  [VantifyOperation.FETCH_SITE_TENANTS]: false,
  [VantifyOperation.FETCH_CATEGORIES]: false,
  [VantifyOperation.FETCH_SUBCATEGORIES]: false,
  [VantifyOperation.FETCH_SERVICE_PROVIDERS]: false,
  [VantifyOperation.FETCH_SITE_ASSOCIATES]: false,
  [VantifyOperation.ACCEPT_WORK_ORDER_APPROVAL]: false,
};

export const errorInitial: Record<LoadingKeys, string | null> = {
  [VantifyOperation.FETCH_USERS]: null,
  [VantifyOperation.FETCH_SITES]: null,
  [VantifyOperation.FETCH_SITE_DETAILS]: null,
  [VantifyOperation.FETCH_WORK_ORDER_DETAILS]: null,
  [VantifyOperation.FETCH_WORK_ORDERS]: null,
  [VantifyOperation.FETCH_WORK_ORDER_FILES]: null,
  [VantifyOperation.CREATE_WORK_ORDER]: null,
  [VantifyOperation.CANCEL_WORK_ORDER]: null,
  [VantifyOperation.ADD_WORK_ORDER_ATTACHMENT]: null,
  [VantifyOperation.DELETE_WORK_ORDER_ATTACHMENT]: null,
  [VantifyOperation.UPLOAD_FILE]: null,
  [VantifyOperation.FETCH_SITE_TENANTS]: null,
  [VantifyOperation.FETCH_CATEGORIES]: null,
  [VantifyOperation.FETCH_SUBCATEGORIES]: null,
  [VantifyOperation.FETCH_SERVICE_PROVIDERS]: null,
  [VantifyOperation.FETCH_SITE_ASSOCIATES]: null,
  [VantifyOperation.ACCEPT_WORK_ORDER_APPROVAL]: null,
};

export const hookMessages = {
  error_FETCH_USERS: 'Failed to fetch users',
  error_FETCH_SITES: 'Failed to fetch sites',
  error_FETCH_SITE_DETAILS: 'Failed to fetch site details',
  error_FETCH_WORK_ORDER_DETAILS: 'Failed to fetch work order details',
  error_FETCH_WORK_ORDERS: 'Failed to fetch work orders',
  error_FETCH_WORK_ORDER_FILES: 'Failed to fetch work order files',
  error_CREATE_WORK_ORDER: 'Failed to create work order',
  error_CANCEL_WORK_ORDER: 'Failed to cancel work order',
  error_ADD_WORK_ORDER_ATTACHMENT: 'Failed to add work order attachment',
  error_DELETE_WORK_ORDER_ATTACHMENT: 'Failed to delete work order attachment',
  error_UPLOAD_FILE: 'Failed to upload file',
  error_FETCH_SITE_TENANTS: 'Failed to fetch site tenants',
  error_FETCH_CATEGORIES: 'Failed to fetch categories',
  error_FETCH_SUBCATEGORIES: 'Failed to fetch subcategories',
  error_FETCH_SERVICE_PROVIDERS: 'Failed to fetch service providers',
  error_FETCH_SITE_ASSOCIATES: 'Failed to fetch site associates',
  error_ACCEPT_WORK_ORDER_APPROVAL: 'Failed to accept work order approval',
};
